import { useEffect, useRef, useState } from 'react';
import { Trash2Icon } from 'lucide-react';
type Props = {
  onConfirm: () => void;
  label?: string;
  /** Waarschuwing bij de bevestiging, bijv. "Zit in 2 protocollen". */
  warning?: string;
  /** Verwijderen kan nu niet (bijv. nog gekoppeld): de uitleg staat er, zonder "Ja, verwijder". */
  blockedReason?: string;
  /**
   * De aanroeper biedt daarna "Ongedaan maken" (undoToast). Dan is één klik genoeg: vragen én
   * herstellen is dubbele frictie. Met een waarschuwing blijft de bevestiging staan, want die
   * zegt iets dat je vóór het klikken moet weten.
   */
  undoable?: boolean;
};

/** Zo lang blijft "Zeker weten?" staan voordat de knop vanzelf terugvalt. */
const ARMED_MS = 5000;
const idleCls = 'inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[14px] text-muted transition-colors duration-150 ease-soft hover:bg-blush/60 hover:text-ink';

/**
 * Verwijderen zonder dat het per ongeluk onherroepelijk is. Twee vormen:
 * - te herstellen (concept, `undoable`): direct weg, met "Ongedaan maken" in een melding;
 * - niet te herstellen (gaat meteen naar de server of naar de klant): eerst "Zeker weten?".
 */
export function DeleteButton({
  onConfirm,
  label = 'Verwijderen',
  warning,
  blockedReason,
  undoable = false
}: Props) {
  const [armed, setArmed] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const direct = undoable && !warning && !blockedReason;
  useEffect(() => {
    if (!armed) return;
    // Toetsenbord: de focus gaat naar "Annuleer", zodat Enter nooit per ongeluk verwijdert.
    cancelRef.current?.focus();
    const t = window.setTimeout(() => setArmed(false), ARMED_MS);
    return () => window.clearTimeout(t);
  }, [armed]);
  if (!armed) {
    return <button type="button" onClick={() => direct ? onConfirm() : setArmed(true)} className={idleCls} data-id="element-4981" data-mp="lcl2bsjofoe4">
        <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4982" data-mp="3axprwon3cpz0" />
        {label}
      </button>;
  }
  return <span role="group" aria-label="Verwijderen bevestigen" className="inline-flex w-full flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-2xl bg-blush/60 py-2 pl-4 pr-2 sm:w-auto sm:rounded-full sm:py-1 sm:pr-1" data-id="element-4983" data-mp="1y1jxs9vhree3">
      <span className="flex-1 text-[13px] text-ink sm:flex-none" role="status" data-id="element-4984" data-mp="1o2ktf3hre4ww">
        {blockedReason ?? warning ?? 'Zeker weten?'}
      </span>
      <span className="flex items-center gap-1" data-id="element-4985" data-mp="1edjhppi8y9oq">
        <button ref={cancelRef} type="button" onClick={() => setArmed(false)} className="rounded-full px-3 py-2 text-[13px] text-muted hover:text-ink sm:py-1.5" data-id="element-4986" data-mp="1ebqaa7zt7lfo">
          {blockedReason ? 'Oké' : 'Annuleer'}
        </button>
        {!blockedReason && <button type="button" onClick={onConfirm} className="rounded-full bg-ink px-3.5 py-2 text-[13px] text-shell transition-colors duration-150 ease-soft hover:bg-clay sm:py-1.5" data-id="element-4987" data-mp="1ab6bkzzl89rf">
            Ja, verwijder
          </button>}
      </span>
    </span>;
}